import { getHeaders } from './streaksApi';

const BASE_URL = 'https://backend.testeswaffle.org/utils';

export interface ShortUrlResponse {
    shortId: string;
    longUrl: string;
}

export interface UrlClickInfo {
    shortId: string;
    longUrl: string;
    clicks: number;
    createdAt: string;
}

export interface ClicksResponse {
    total: number;
    urls: UrlClickInfo[];
}

export interface ApiError {
    error: string;
}

const MAX_RETRIES = 2;
const INITIAL_RETRY_DELAY = 2000; // 2 segundos
const MAX_RETRY_DELAY = 10000; // 10 segundos
const BACKOFF_FACTOR = 1.5;

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const getRetryDelay = (attempt: number): number => {
    const exponentialDelay = Math.min(
        INITIAL_RETRY_DELAY * Math.pow(BACKOFF_FACTOR, attempt),
        MAX_RETRY_DELAY
    );
    return exponentialDelay + Math.random() * 1000;
};

const fetchWithRetry = async (url: string, options: RequestInit, maxRetries = MAX_RETRIES): Promise<Response> => {
    let lastError: Error | null = null;

    for (let attempt = 0; attempt < maxRetries; attempt++) {
        try {
            const response = await fetch(url, options);
            
            if (response.ok || (response.status !== 429 && response.status !== 503)) {
                return response;
            }
            
            // Se for erro de recursos ou rate limit
            if (attempt < maxRetries - 1) {
                const retryDelay = getRetryDelay(attempt);
                console.warn(`Tentativa ${attempt + 1}/${maxRetries} falhou. Aguardando ${Math.round(retryDelay)}ms antes de tentar novamente.`);
                await delay(retryDelay);
            }
            
        } catch (error) {
            lastError = error as Error;
            
            if (attempt < maxRetries - 1) {
                const retryDelay = getRetryDelay(attempt);
                console.warn(`Erro na tentativa ${attempt + 1}/${maxRetries}. Aguardando ${Math.round(retryDelay)}ms antes de tentar novamente.`);
                await delay(retryDelay);
            }
        }
    }
    
    throw lastError || new Error('Máximo de tentativas excedido');
};

export const createShortUrl = async (longUrl: string): Promise<ShortUrlResponse | ApiError> => {
    try {
        const headers = await getHeaders();
        const response = await fetchWithRetry(`${BASE_URL}/create/short-url`, {
            method: 'POST',
            headers: {
                ...headers,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ longUrl })
        });
        
        const data = await response.json();
        
        if (!response.ok) {
            return data as ApiError;
        }
        
        return data as ShortUrlResponse;
    } catch (error) {
        return { error: 'Erro ao criar URL curta' };
    }
};

export const getUrlClicks = async (): Promise<ClicksResponse | ApiError> => {
    try {
        const headers = await getHeaders();
        const response = await fetchWithRetry(`${BASE_URL}/info/short-url`, {
            method: 'GET',
            headers
        });
        
        const data = await response.json();
        
        if (!response.ok) {
            return data as ApiError;
        }
        
        return data as ClicksResponse;
    } catch (error) {
        return { error: 'Erro ao obter estatísticas de cliques' };
    }
};

export const getLongUrl = async (shortId: string): Promise<ShortUrlResponse | ApiError> => {
    try {
        const headers = await getHeaders();
        const response = await fetchWithRetry(`${BASE_URL}/short-url/${shortId}`, {
            method: 'GET',
            headers
        });
        
        const data = await response.json();
        
        if (!response.ok) {
            return data as ApiError;
        }
        
        return data as ShortUrlResponse;
    } catch (error) {
        return { error: 'Erro ao obter URL original' };
    }
}; 